<template>
  <div class="def-sku-weights">
    <button
      v-for="item in getUniqItems"
      :key="item.ID"
      type="button"
      class="sku-weight"
      :class="{
        'sku-weight_active': item.active,
        'sku-weight_disabled': item.disable || item.onOrder,
      }"
      @click="handlerChangeWeight(item, product)"
    >
      {{ item.printWeight }}
    </button>
  </div>
</template>

<script lang="ts" setup>
import { IProduct, IProductWeight } from '@/storePinia/interfaces/IProduct/IProduct';
import { useSKU } from '@use/useSKU/useSKU';
import { computed } from 'vue';

const props = defineProps<{
  product: IProduct;
  weights?: Array<IProductWeight>;
}>();
const emit = defineEmits(['changeWeight']);

const sku = useSKU();

const handlerChangeWeight = async (item, product) => {
  const response = await sku.selectWeight(item, product);

  if (response?.product) {
    emit('changeWeight', response?.product);
  }
};

const items = computed(() => props.weights ?? props.product.weights?.items);

const getUniqItems = computed(() => {
  // фільтруємо для товарів у яких може бути 2 однакових ваги.
  // якщо серед дублів є активний (напр. вага з намеку) - залишаємо саме його, зберігаючи позицію першого
  if (!items.value) return items.value;

  const uniqWeights = new Map<string, IProductWeight>();

  items.value.forEach((item) => {
    const weight = String(item.weight);
    const existing = uniqWeights.get(weight);

    if (!existing || (!existing.active && item.active)) {
      uniqWeights.set(weight, item);
    }
  });

  return Array.from(uniqWeights.values());
});
</script>

<style lang="scss" scoped>
.def-sku-weights {
  display: grid;
  grid-template-columns: repeat(4, minmax(32px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
  @include point-md {
    display: grid;
    grid-template-columns: repeat(5, minmax(32px, 1fr));
  }
}
.sku-weight {
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #cacece;
  border-radius: 100px;
  height: 32px;
  padding: 0 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 125%;
  text-transform: uppercase;
  &_active {
    background-color: var(--background-black);
    color: #fff;
    pointer-events: none;
  }
  &_disabled {
    color: var(--color-dark-grey);
    border-color: var(--color-dark-grey);
    position: relative;
    overflow: hidden;
    &:after {
      content: '';
      height: 100px;
      width: 1px;
      background-color: currentColor;
      position: absolute;
      transform: rotate(65deg);
      left: 50%;
    }
  }
}
</style>
